import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { isAuthenticated, isAdmin, booting, user } = useAuth();
  const location = useLocation();

  if (booting) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-bg-primary"
        data-testid="app-booting"
      >
        <div className="flex flex-col items-center gap-3 text-text-secondary">
          <div className="h-8 w-8 rounded-full border-2 border-accent-pink border-t-transparent animate-spin" />
          <div className="text-[13px]">Loading…</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Admins land on their own shell — the user-facing dashboard is not for them.
  if (isAdmin && (location.pathname === "/" || location.pathname === "/dashboard")) {
    return <Navigate to="/admin" replace />;
  }

  // Phase 10E — mandatory 2FA setup: if the user hasn't enrolled any 2FA
  // method yet, force them through /setup-2fa before anything else (including
  // onboarding). Admins are exempt (their 2FA state is managed separately).
  const enrolled = Array.isArray(user?.enrolled_2fa_channels) ? user.enrolled_2fa_channels : [];
  const needs2FASetup = !isAdmin && user && enrolled.length === 0;
  if (needs2FASetup && location.pathname !== "/setup-2fa") {
    return <Navigate to="/setup-2fa" replace />;
  }

  // Onboarding redirect — only AFTER 2FA setup is done (don't bounce
  // /setup-2fa users to /onboarding).
  if (
    !needs2FASetup &&
    requireOnboarding &&
    user &&
    user.onboarding_completed === false &&
    !user.onboarding_deferred
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

export default ProtectedRoute;
